import React, { useEffect, useState } from 'react';
import {
  Title,
  Group,
  Button,
  TextInput,
  Textarea,
  Select,
  Stack,
  Grid,
  Card,
  ActionIcon,
  LoadingOverlay,
  Alert,
  Text,
  NumberInput,
  Switch,
  Slider,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconCamera,
  IconMapPin,
  IconVideo,
  IconSettings,
  IconTestPipe,
} from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { useNavigate, useParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useCamera, useCreateCamera, useUpdateCamera } from '../../hooks/useCameras';
import { useAreas } from '../../hooks/useAreas';
import { useRequireSchoolId } from '../../contexts/SchoolContext';

interface CameraFormData {
  name: string;
  description?: string;
  rtsp_url: string;
  area_id: string | null;
  resolution: string;
  frame_rate: number;
  is_active: boolean;
  position?: {
    x?: number;
    y?: number;
    z?: number;
    rotation?: number;
  };
  settings?: {
    motion_detection_enabled: boolean;
    night_vision_enabled: boolean;
    audio_recording_enabled: boolean;
    detection_sensitivity: number;
    recording_quality: string;
  };
}

const CameraForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = id !== 'new' && !!id;
  const schoolId = useRequireSchoolId();
  
  const [testingConnection, setTestingConnection] = useState(false);

  const {
    data: camera,
    isLoading: cameraLoading,
    error: cameraError,
  } = useCamera(id || '', isEditing ? undefined : '');

  const { data: areasData } = useAreas({ per_page: 100 });

  const createCameraMutation = useCreateCamera();
  const updateCameraMutation = useUpdateCamera();

  const form = useForm<CameraFormData>({
    initialValues: {
      name: '',
      description: '',
      rtsp_url: '',
      area_id: null,
      resolution: '1920x1080',
      frame_rate: 30,
      is_active: true,
      position: {
        x: 0,
        y: 0,
        z: 0,
        rotation: 0,
      },
      settings: {
        motion_detection_enabled: true,
        night_vision_enabled: false,
        audio_recording_enabled: false,
        detection_sensitivity: 50,
        recording_quality: 'high',
      },
    },
    validate: {
      name: (value) => (!value.trim() ? 'Camera name is required' : null),
      rtsp_url: (value) => {
        if (!value.trim()) return 'RTSP URL is required';
        if (!value.startsWith('rtsp://')) return 'URL must start with rtsp://';
        return null;
      },
      area_id: (value) => (!value ? 'Area assignment is required' : null),
      frame_rate: (value) => {
        if (!value || value < 1 || value > 60) {
          return 'Frame rate must be between 1 and 60 FPS';
        }
        return null;
      },
    },
  });

  const areas = areasData?.areas || [];

  // Populate form when editing
  useEffect(() => {
    if (isEditing && camera) {
      form.setValues({
        name: camera.name || '',
        description: camera.description || '',
        rtsp_url: camera.rtsp_url || '',
        area_id: camera.area_id || null,
        resolution: camera.resolution || '1920x1080',
        frame_rate: camera.frame_rate || 30,
        is_active: camera.is_active ?? true,
        position: camera.position || {
          x: 0,
          y: 0,
          z: 0,
          rotation: 0,
        },
        settings: {
          motion_detection_enabled: camera.settings?.motion_detection_enabled ?? true,
          night_vision_enabled: camera.settings?.night_vision_enabled ?? false,
          audio_recording_enabled: camera.settings?.audio_recording_enabled ?? false,
          detection_sensitivity: camera.settings?.detection_sensitivity ?? 50,
          recording_quality: camera.settings?.recording_quality || 'high',
        },
      });
    }
  }, [camera, isEditing]);

  const handleSubmit = async (values: CameraFormData) => {
    try {
      const submitData = {
        name: values.name,
        description: values.description,
        rtsp_url: values.rtsp_url,
        area_id: values.area_id || '0',
        resolution: values.resolution,
        frame_rate: values.frame_rate,
        is_active: values.is_active,
        position: values.position,
        settings: values.settings,
      };

      if (isEditing) {
        await updateCameraMutation.mutateAsync({
          id: id!,
          data: submitData,
        });
        notifications.show({
          title: 'Success',
          message: 'Camera updated successfully',
          color: 'green',
          icon: <IconCheck size={16} />,
        });
      } else {
        const response = await createCameraMutation.mutateAsync({
          ...submitData,
          school_id: schoolId,
        });
        notifications.show({
          title: 'Success',
          message: 'Camera added successfully',
          color: 'green',
          icon: <IconCheck size={16} />,
        });
        navigate(`/admin/cameras/${response.id}`);
        return;
      }
      
      navigate('/admin/cameras');
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: isEditing ? 'Failed to update camera' : 'Failed to add camera',
        color: 'red',
        icon: <IconX size={16} />,
      });
    }
  };

  const handleTestConnection = async () => {
    const rtspUrl = form.values.rtsp_url;
    if (!rtspUrl.trim()) {
      notifications.show({
        title: 'Error',
        message: 'Please enter an RTSP URL first',
        color: 'red',
      });
      return;
    }

    setTestingConnection(true);
    try {
      // Simulate connection test since we don't have a real endpoint for this
      await new Promise(resolve => setTimeout(resolve, 2000));
      notifications.show({
        title: 'Connection Test',
        message: 'RTSP URL appears to be valid',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Connection Test Failed',
        message: 'Unable to connect to the RTSP stream',
        color: 'red',
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleCancel = () => {
    if (isEditing) {
      navigate(`/admin/cameras/${id}`);
    } else {
      navigate('/admin/cameras');
    }
  };

  const isLoading = createCameraMutation.isPending || updateCameraMutation.isPending;

  if (isEditing && cameraError) {
    return (
      <Alert
        icon={<IconAlertTriangle size={16} />}
        title="Error loading camera"
        color="red"
      >
        Failed to load camera details. Please try again.
      </Alert>
    );
  }

  if (isEditing && cameraLoading) {
    return <LoadingOverlay visible />;
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <ActionIcon variant="subtle" onClick={handleCancel}>
              <IconArrowLeft size={16} />
            </ActionIcon>
            <div>
              <Title order={2}>
                {isEditing ? 'Edit Camera' : 'Add New Camera'}
              </Title>
              <Text size="sm" c="dimmed">
                {isEditing ? 'Update camera configuration' : 'Configure a new security camera'}
              </Text>
            </div>
          </Group>
          <Group>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isLoading}
              leftSection={<IconCheck size={16} />}
            >
              {isEditing ? 'Update Camera' : 'Add Camera'}
            </Button>
          </Group>
        </Group>

        <Grid>
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Stack gap="md">
              {/* Basic Information */}
              <Card>
                <Group mb="md">
                  <IconCamera size={20} color="blue" />
                  <Title order={4}>Basic Information</Title>
                </Group>

                <Stack gap="md">
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Camera Name"
                        placeholder="Enter camera name"
                        required
                        {...form.getInputProps('name')}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Select
                        label="Area Assignment"
                        placeholder="Select area"
                        required
                        data={areas
                          .filter((area: any) => area && area.name)
                          .map((area: any) => ({
                            value: area.id.toString(),
                            label: `${area.name} (${area.area_type})`,
                          }))}
                        {...form.getInputProps('area_id')}
                      />
                    </Grid.Col>
                  </Grid>

                  <Textarea
                    label="Description (Optional)"
                    placeholder="Enter camera description"
                    autosize
                    minRows={2}
                    maxRows={4}
                    {...form.getInputProps('description')}
                  />
                </Stack>
              </Card>

              {/* Network Configuration */}
              <Card>
                <Group mb="md">
                  <IconVideo size={20} color="green" />
                  <Title order={4}>Network Configuration</Title>
                </Group>

                <Stack gap="md">
                  <Group align="flex-end">
                    <TextInput
                      label="RTSP URL"
                      placeholder="rtsp://camera.ip.address:554/stream"
                      required
                      style={{ flex: 1 }}
                      {...form.getInputProps('rtsp_url')}
                    />
                    <Button
                      variant="outline"
                      leftSection={<IconTestPipe size={16} />}
                      onClick={handleTestConnection}
                      loading={testingConnection}
                    >
                      Test Connection
                    </Button>
                  </Group>

                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Select
                        label="Resolution"
                        data={[
                          { value: '640x480', label: '640x480 (VGA)' },
                          { value: '1280x720', label: '1280x720 (HD)' },
                          { value: '1920x1080', label: '1920x1080 (Full HD)' },
                          { value: '2560x1440', label: '2560x1440 (2K)' },
                          { value: '3840x2160', label: '3840x2160 (4K)' },
                        ]}
                        {...form.getInputProps('resolution')}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <NumberInput
                        label="Frame Rate (FPS)"
                        placeholder="30"
                        min={1}
                        max={60}
                        {...form.getInputProps('frame_rate')}
                      />
                    </Grid.Col>
                  </Grid>
                </Stack>
              </Card>

              {/* Position Configuration */}
              <Card>
                <Group mb="md">
                  <IconMapPin size={20} color="orange" />
                  <Title order={4}>Position & Orientation</Title>
                </Group>

                <Grid>
                  <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                    <NumberInput
                      label="X Position"
                      placeholder="0"
                      {...form.getInputProps('position.x')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                    <NumberInput
                      label="Y Position"
                      placeholder="0"
                      {...form.getInputProps('position.y')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                    <NumberInput
                      label="Z Position (Height)"
                      placeholder="0"
                      {...form.getInputProps('position.z')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                    <NumberInput
                      label="Rotation (Degrees)"
                      placeholder="0"
                      min={0}
                      max={360}
                      {...form.getInputProps('position.rotation')}
                    />
                  </Grid.Col>
                </Grid>
              </Card>

              {/* Detection Settings */}
              <Card>
                <Group mb="md">
                  <IconSettings size={20} color="purple" />
                  <Title order={4}>Detection & Recording Settings</Title>
                </Group>

                <Stack gap="md">
                  <Switch
                    label="Motion Detection"
                    description="Enable automatic motion detection"
                    {...form.getInputProps('settings.motion_detection_enabled', { type: 'checkbox' })}
                  />

                  <div>
                    <Text size="sm" mb="xs">Detection Sensitivity</Text>
                    <Slider
                      {...form.getInputProps('settings.detection_sensitivity')}
                      min={0}
                      max={100}
                      step={10}
                      marks={[
                        { value: 0, label: 'Low' },
                        { value: 50, label: 'Medium' },
                        { value: 100, label: 'High' }
                      ]}
                    />
                  </div>

                  <Switch
                    label="Night Vision"
                    description="Enable infrared night vision mode"
                    {...form.getInputProps('settings.night_vision_enabled', { type: 'checkbox' })}
                  />

                  <Switch
                    label="Audio Recording"
                    description="Record audio along with video"
                    {...form.getInputProps('settings.audio_recording_enabled', { type: 'checkbox' })}
                  />

                  <Select
                    label="Recording Quality"
                    data={[
                      { value: 'low', label: 'Low Quality' },
                      { value: 'medium', label: 'Medium Quality' },
                      { value: 'high', label: 'High Quality' },
                      { value: 'ultra', label: 'Ultra Quality' },
                    ]}
                    {...form.getInputProps('settings.recording_quality')}
                  />
                </Stack>
              </Card>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Stack gap="md">
              {/* Status & Settings */}
              <Card>
                <Title order={4} mb="md">Camera Status</Title>
                
                <Switch
                  label="Camera Active"
                  description="Enable or disable this camera"
                  {...form.getInputProps('is_active', { type: 'checkbox' })}
                />
              </Card>

              {/* Help Information */}
              <Card>
                <Title order={4} mb="md">Configuration Guide</Title>
                <Stack gap="xs">
                  <Text size="sm" c="dimmed">
                    • RTSP URL format: rtsp://ip:port/path
                  </Text>
                  <Text size="sm" c="dimmed">
                    • Higher resolution requires more bandwidth
                  </Text>
                  <Text size="sm" c="dimmed">
                    • Test connection before saving
                  </Text>
                  <Text size="sm" c="dimmed">
                    • Position coordinates help with area mapping
                  </Text>
                  <Text size="sm" c="dimmed">
                    • Motion detection reduces false alerts
                  </Text>
                </Stack>
              </Card>

              {isEditing && camera && (
                <Card>
                  <Title order={4} mb="md">Camera Information</Title>
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Created</Text>
                      <Text size="sm">
                        {new Date(camera.created_at).toLocaleDateString()}
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Last Updated</Text>
                      <Text size="sm">
                        {new Date(camera.updated_at).toLocaleDateString()}
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Detections</Text>
                      <Text size="sm">{camera.detections_count || 0}</Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Status</Text>
                      <Text size="sm" tt="capitalize">{camera.status}</Text>
                    </Group>
                  </Stack>
                </Card>
              )}
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>
    </form>
  );
};

export default CameraForm;